import { useMatch, useSearch } from '@tanstack/react-router';
import type { Id } from '@repo/backend/convex/_generated/dataModel';
import type { DocumentKind } from '@/components/search/useDocumentSearch';
import { filterTagId, type TagFilter } from '@/components/tags/tagFilter';

/**
 * The Library's URL state: `?tag=` is the collection, `?kind=` the type filter.
 * The tag is a plain string because the router can't serialise branded ids;
 * `UNTAGGED` stands for documents with no tags.
 */
export type LibrarySearch = { kind?: DocumentKind; tag?: string };

export type LibraryType = { label: string; value: 'all' | DocumentKind };

/** Options for the Library's type filter; 'all' means no `?kind=`. */
export const LIBRARY_TYPES: LibraryType[] = [
  { label: 'All types', value: 'all' },
  { label: 'Documents', value: 'upload' },
  { label: 'Notes', value: 'note' },
];

export const parseLibraryKind = (value: unknown): DocumentKind | undefined =>
  value === 'note' || value === 'upload' ? value : undefined;

export const parseLibraryTag = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

/** The tag filter in the URL, typed for Convex. */
export const libraryTagFilter = (search: LibrarySearch | null): TagFilter | undefined =>
  search?.tag as TagFilter | undefined;

/** The open collection's tag id; undefined when showing untagged documents. */
export const libraryTagId = (search: LibrarySearch | null): Id<'tags'> | undefined =>
  filterTagId(libraryTagFilter(search));

/**
 * The Library's filters when the Library list is showing, or null on any other
 * page (e.g. a document opened from the Library), so nothing reads as active.
 */
export function useLibrarySearch(): LibrarySearch | null {
  const match = useMatch({ from: '/$spaceSlug/library', shouldThrow: false });
  const search = useSearch({ strict: false }) as LibrarySearch;
  return match ? { kind: search.kind, tag: search.tag } : null;
}
